import { useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { cx } from 'class-variance-authority';
import { format } from 'date-fns';
import type { RootState } from '@redux/RootReducer';

import { useAuth } from '@nvision/identity-core';
import {
  Button,
  type ColumnDef,
  DataTable,
  Icon,
  Modal,
  Pagination,
  SELECTED_ALL_ID,
  Spinner,
  Text,
} from '@nvision/ui';

import { useCompanyUsers } from '@/modules/entities';
import { NoData } from '@/modules/errors';
import {
  getDefaultPricingsFormValues,
  normalizePricingsFiltersUpdate,
  type Pricing,
  type PricingsFilters,
  type PricingsFiltersApi,
  PricingsSearchFiltersForm,
  type PricingsSearchFiltersFormRef,
  usePricings,
} from '@/modules/pricing';

import styles from './pricing-search-modal.module.css';

const DEFAULT_TAKE = 10;
const DATE_FORMAT = 'dd-MMM-yyyy';
const EMPTY_VALUE = '—';

interface PricingSearchModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectionChange?: (value: Pricing[]) => void;
}

const formatDateValue = (value: string | null | undefined) => (value ? format(value, DATE_FORMAT) : EMPTY_VALUE);

const getColumns = (usersById: Record<string, { fullName?: string }>): ColumnDef<Pricing>[] => [
  {
    accessorKey: 'pricingKey',
    cell: (props) => props.row.original.pricingKey,
    enableSorting: false,
    header: 'Pricing Key',
    size: 120,
  },
  {
    accessorKey: 'carrier',
    cell: (props) => props.row.original.carrier?.label || EMPTY_VALUE,
    enableSorting: false,
    header: 'Transportation Provider',
    size: 200,
  },
  {
    accessorKey: 'branch',
    cell: (props) => props.row.original.branch?.label || EMPTY_VALUE,
    enableSorting: false,
    header: 'Branch',
    size: 140,
  },
  {
    accessorKey: 'pricingType',
    cell: (props) => props.row.original.pricingType?.label || EMPTY_VALUE,
    enableSorting: false,
    header: 'Pricing Type',
    size: 160,
  },
  {
    accessorKey: 'cargoType',
    cell: (props) => props.row.original.cargoType?.label || EMPTY_VALUE,
    enableSorting: false,
    header: 'Cargo Type',
    size: 130,
  },
  {
    accessorKey: 'pricingBasis',
    cell: (props) => props.row.original.pricingBasis?.label || EMPTY_VALUE,
    enableSorting: false,
    header: 'Pricing Basis',
    size: 150,
  },
  {
    accessorKey: 'currency',
    cell: (props) => props.row.original.currency?.label || EMPTY_VALUE,
    enableSorting: false,
    header: 'Currency',
    size: 120,
  },
  {
    accessorKey: 'transportationMode',
    cell: (props) => props.row.original.transportationMode?.label || EMPTY_VALUE,
    enableSorting: false,
    header: 'Mode',
    size: 120,
  },
  {
    accessorKey: 'packingCode',
    cell: (props) => props.row.original.packingCode?.label || EMPTY_VALUE,
    enableSorting: false,
    header: 'Package Type',
    size: 140,
  },
  {
    accessorKey: 'equipmentType',
    cell: (props) => props.row.original.equipmentType?.label || EMPTY_VALUE,
    enableSorting: false,
    header: 'Equipment Type',
    size: 150,
  },
  {
    accessorKey: 'service',
    cell: (props) => props.row.original.service?.label || EMPTY_VALUE,
    enableSorting: false,
    header: 'Service',
    size: 160,
  },
  {
    accessorKey: 'effectiveDate',
    cell: (props) => formatDateValue(props.row.original.effectiveDate),
    enableSorting: false,
    header: 'Effective From',
    size: 140,
  },
  {
    accessorKey: 'expirationDate',
    cell: (props) => formatDateValue(props.row.original.expirationDate),
    enableSorting: false,
    header: 'Effective To',
    size: 140,
  },
  {
    accessorKey: 'createdBy',
    cell: (props) =>
      usersById[String(props.row.original.createdBy)]?.fullName ?? props.row.original.createdBy ?? EMPTY_VALUE,
    enableSorting: false,
    header: 'Created By',
    size: 160,
  },
  {
    accessorKey: 'createdOn',
    cell: (props) => formatDateValue(props.row.original.createdOn),
    enableSorting: false,
    header: 'Created On',
    size: 140,
  },
  {
    accessorKey: 'modifiedBy',
    cell: (props) =>
      usersById[String(props.row.original.modifiedBy)]?.fullName ?? props.row.original.modifiedBy ?? EMPTY_VALUE,
    enableSorting: false,
    header: 'Modified By',
    size: 160,
  },
  {
    accessorKey: 'modifiedOn',
    cell: (props) => formatDateValue(props.row.original.modifiedOn),
    enableSorting: false,
    header: 'Modified On',
    size: 140,
  },
];

export const PricingSearchModal: React.FC<PricingSearchModalProps> = ({ isOpen, setIsOpen, onSelectionChange }) => {
  const searchFormRef = useRef<PricingsSearchFiltersFormRef>({} as PricingsSearchFiltersFormRef);
  const auth = useAuth();
  const timeZone = auth.user?.profile.pref.timezone;
  const [params, setParams] = useState<PricingsFiltersApi>();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const { data: pricing, isFetching } = usePricings(params);
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);
  const customerKey = selectedCompany?.companyId ?? 0;

  const skip = params?.skip ?? 0;
  const take = params?.take ?? DEFAULT_TAKE;
  const page = skip / take + 1;
  const items = pricing?.items ?? [];
  const totalCount = pricing?.totalCount ?? 0;

  const userIds = useMemo(
    () =>
      items
        .flatMap((item) => [item.createdBy, item.modifiedBy])
        .filter((id) => id !== null && id !== undefined)
        .map(String),
    [items],
  );

  const { data: companyUsers } = useCompanyUsers({
    companyId: String(selectedCompany?.companyId ?? ''),
    searchParams: { ids: userIds },
  });

  const columns = useMemo(() => getColumns(companyUsers.map), [companyUsers.map]);

  const reset = () => {
    setSelectedItems([]);
    setParams(undefined);
  };

  const close = () => {
    searchFormRef.current?.reset(getDefaultPricingsFormValues(timeZone));
    reset();
    setIsOpen(false);
  };

  const handleSubmit = (values: PricingsFilters) => {
    setSelectedItems([]);
    setParams((prev) => ({
      ...normalizePricingsFiltersUpdate(values),
      skip: 0,
      take: prev?.take ?? DEFAULT_TAKE,
    }));
  };

  const handleSubmitSelection = () => {
    if (selectedItems.length === 0) {
      return;
    }

    const selectedRows =
      selectedItems[0] === SELECTED_ALL_ID ? items : items.filter((item) => selectedItems.includes(item.id));

    onSelectionChange?.(selectedRows);
    close();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(value) => (value ? setIsOpen(true) : close())} className={styles.modal}>
      <Modal.Header className={styles.header}>
        <Text size="xlg" weight="semibold">
          Pricing Search
        </Text>
        <Icon name="x" onClick={close} cursor="pointer" />
      </Modal.Header>
      <Modal.Content className={styles.content}>
        <PricingsSearchFiltersForm
          variant="modal"
          customerKey={customerKey}
          formRef={searchFormRef}
          initialValues={getDefaultPricingsFormValues(timeZone)}
          onSubmit={handleSubmit}
          onReset={reset}
        />
        {isFetching ? (
          <div className={cx(styles.tableContainer, styles.loader)}>
            <Spinner size="lg" />
          </div>
        ) : items.length ? (
          <div className={styles.results}>
            <div className={styles.total}>
              {totalCount === 1 ? (
                <>
                  <Text weight="bold">{`${totalCount} result `}</Text>is found
                </>
              ) : (
                <>
                  <Text weight="bold">{`${totalCount} results `}</Text>are found
                </>
              )}
            </div>
            <div className={styles.tableContainer}>
              <DataTable<Pricing>
                columns={columns}
                data={items}
                isLoading={isFetching}
                page={page}
                limit={take}
                totalItems={totalCount}
                selectedItems={selectedItems}
                onSelectionChange={setSelectedItems}
                columnPinning={{ left: ['pricingKey'] }}
                scrollable
              />
            </div>
            <Pagination
              className={styles.pagination}
              totalItems={totalCount}
              limit={take}
              page={page}
              onPageChange={(page: number) => {
                setParams((prev) => (prev ? { ...prev, skip: (page - 1) * (prev.take ?? DEFAULT_TAKE) } : prev));
              }}
              onOffsetChange={(offset: number) => setParams((prev) => (prev ? { ...prev, skip: offset } : prev))}
              onLimitChange={(limit: number) => setParams((prev) => (prev ? { ...prev, skip: 0, take: limit } : prev))}
            />
          </div>
        ) : (
          <NoData
            className={styles.noData}
            description="No data to display. Please use the search above to find pricing."
          />
        )}
      </Modal.Content>
      <Modal.Footer className={styles.footer}>
        <Button outline secondary onClick={close}>
          Cancel
        </Button>
        <Button isDisabled={selectedItems.length === 0} onClick={handleSubmitSelection}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
